// Privacy policy and terms for the q2cue.app installation, public at /privacy and /terms.
// Every statement here must match the code and docs/hosted/*.md; update both together.

import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { Logo, SiteFooter } from "./Landing.tsx";

const UPDATED = "31 October 2026";
const CONTACT = "hello@q2cue.app";

function LegalPage(props: { title: string; children: ComponentChildren }) {
  useEffect(() => {
    document.title = `${props.title} · q2cue`;
  }, [props.title]);
  return (
    <div class="landing legal" id="top">
      <a class="landing-skip" href="#main">Skip to content</a>
      <header class="landing-head">
        <div class="landing-wrap landing-bar">
          <a href="/" class="brand"><Logo /></a>
          <a class="button" href="/signin">Sign in</a>
        </div>
      </header>
      <main id="main" tabIndex={-1} class="landing-wrap legal-body">
        <h1>{props.title}</h1>
        <p class="legal-updated">Last updated {UPDATED}</p>
        {props.children}
      </main>
      <SiteFooter />
    </div>
  );
}

const Mail = () => <a href={`mailto:${CONTACT}`}>{CONTACT}</a>;

export function Privacy() {
  return (
    <LegalPage title="Privacy policy">
      <p>
        q2cue.app is run by Brendan O'Leary. This page covers that installation. q2cue is open source, and anyone can run their own copy on their own Cloudflare account. If you use someone else's installation, its operator is responsible for your data there, not me.
      </p>
      <p>
        Questions or requests: <Mail />.
      </p>

      <h2>What q2cue stores about you</h2>
      <h3>Your account</h3>
      <ul>
        <li>Your email address and the way you sign in (an email link or Google). An account always signs in the way it was created.</li>
        <li>If you sign in with Google: the name, email address and profile picture link that Google shares, and your Google account ID. Google's sign-in also returns tokens. q2cue stores the access token encrypted, and keeps the ID token (a signed copy of that same name, email and picture link) as Google sent it. It never uses either to call Google.</li>
        <li>Your chosen display name and optional profile photo. You can change or remove the photo from Account on the shows page. Uploaded photos are cropped and resized in your browser; q2cue stores the resized JPEG, not the original file or its metadata.</li>
        <li>Your sessions: a session ID, when it expires, and the IP address and browser description of the device that signed in. A session lasts 14 days from when you sign in, or until you sign out. Expired sessions are deleted daily.</li>
        <li>For a day or two after you ask for a sign-in link or sign in, your email address and IP address are kept in counters that stop too many requests.</li>
      </ul>
      <h3>Your shows</h3>
      <ul>
        <li>The script PDFs you upload or import, exactly as you provided them, with their file names.</li>
        <li>Everything you and your collaborators add to a show: cues, standbys, notes, departments, page labels and the show title.</li>
        <li>A history of every saved change, with who made it and when, and any checkpoints you name.</li>
        <li>Prompt books and cue sheets you export, and a copy of the show as it was when you exported.</li>
        <li>Who belongs to each show, who added them, their role, and invitations you send or receive.</li>
      </ul>
      <p>
        If two people upload the identical PDF file, q2cue keeps one copy and both shows point to it. Neither person can see the other's show because of this.
      </p>

      <h2>Who can see it</h2>
      <ul>
        <li>Everyone in a show can see the whole show, including every department, note layer, its history and its member list. The member list shows each member's name and email address. Viewers can see but not change anything.</li>
        <li>People in a show can see your name next to your changes and checkpoints, and see when you have the show open, which page you're on, and whether you're adding or editing something. Until you choose a display name, email-link accounts use your email address as your name. Profile photos are currently shown only in your Account section.</li>
        <li>When you invite someone, the email they receive includes your name (or email address), the show's title and their role.</li>
        <li>I can access stored data as the operator. I don't look at show content except to fix a problem you report or to restore from a backup, and I never share it.</li>
      </ul>
      <p>
        Your scripts and show data are not used for advertising, sold, or used to train AI models.
      </p>

      <h2>Services q2cue uses</h2>
      <ul>
        <li>
          <strong>Cloudflare</strong> runs the app and stores accounts, show data and PDFs. Cloudflare also handles the q2cue.app domain and forwards mail sent to q2cue.app addresses. When Turnstile bot protection is enabled, the sign-in page loads Cloudflare's verification script; Cloudflare processes browser and interaction signals to distinguish people from automated requests. q2cue sends the verification token and your IP address to Cloudflare to check it, not your script or show contents.
        </li>
        <li>
          <strong>Resend</strong> sends q2cue's emails: sign-in links and sign-in reminders, invitations and email confirmations. Resend receives the recipient's address and the message.
        </li>
        <li>
          <strong>Google</strong>, only if you choose "Continue with Google". q2cue asks Google for your basic profile and email address, nothing else.
        </li>
      </ul>
      <p>There are no advertising or audience-analytics scripts. Fonts are bundled with the app. The sign-in page loads Cloudflare Turnstile when enabled; other application scripts are served by q2cue.</p>

      <h2>Cookies and storage on your device</h2>
      <p>
        q2cue sets one cookie to keep you signed in, and a short-lived one during Google sign-in. Neither is used for tracking.
      </p>
      <p>
        When you open a show, your browser keeps a copy of the script and the show so you can work without a connection, and asks the browser not to clear it. Changes you make offline wait in your browser until they can be sent. Your browser also remembers your account name, email address and profile photo, and your view settings.
      </p>
      <p>
        Signing out removes downloaded scripts and saved copies but keeps changes that were never sent, so you don't lose work. You can remove offline copies from the shows page, erase one show's data from that show's settings, or erase all q2cue data in that browser from the sign-in page.
      </p>
      <p>
        q2cue can't reach into your devices or anyone else's. If someone you shared a show with opened it, their copy stays on their device until they remove it, even after you remove them from the show.
      </p>

      <h2>Logs</h2>
      <p>
        Cloudflare records each request to the app for troubleshooting: the time, the page or action requested, your IP address and the response. These logs are kept for up to 7 days. Query strings are removed from them, and q2cue's own error messages leave out sign-in, invitation and file names. They never include what you typed, uploaded or changed in a show.
      </p>

      <h2>How long data is kept</h2>
      <ul>
        <li>Shows, their history and exports: while the show exists, including while it's archived.</li>
        <li>Deleted shows go to the trash for 30 days, where the owner can restore them. After that q2cue deletes the show, its history and its exports. Its PDF is deleted too, unless another show uses the same file.</li>
        <li>Sign-in links work once and expire after 10 minutes. Invitations expire after 7 days; unaccepted ones are deleted 30 days after that.</li>
        <li>
          Backups are taken before upgrades, kept on the operator's own server outside Cloudflare where only the operator can read them, and deleted after 90 days. Deleting a show or account doesn't remove it from backups already taken. If a backup is ever restored, q2cue re-applies the show deletions, account deletions and membership changes made after the backup was taken.
        </li>
        <li>q2cue permanently keeps a record of deletions and access changes (IDs, dates, roles and a fingerprint of each deleted PDF; no names, email addresses or show content) so that a restore can't bring deleted data back.</li>
        <li>Account suspension keeps the account and its shows but blocks server access and ends its sessions. The suspension record stays until the account is reinstated or deleted. Copies already saved on a device cannot be remotely erased.</li>
      </ul>

      <h2>Deleting your account</h2>
      <p>
        You can delete your account from the shows page. First transfer or delete any shows you own. Deleting your account removes your account, email address, name, sessions, memberships and any invitations sent to your address at once, and immediately deletes any of your shows that are in the trash. In shows you were part of, your past changes, their descriptions and your checkpoints stay so the show still makes sense, labelled "Deleted account" instead of your name. Those records, and invitations you sent to others, keep an internal ID that is no longer linked to any name or email address.
      </p>
      <p>
        You can download a show at any time as a bundle containing the original PDF and the show's cues, notes and settings (Menu → Show settings), and import it into any q2cue installation. History, checkpoints and the member list aren't included.
      </p>
      <p>
        To ask for a copy of what q2cue holds about you, a correction, or anything else about your data, email <Mail />.
      </p>

      <h2>Changes</h2>
      <p>If this policy changes in a way that matters, I'll update the date at the top and say what changed in the project's changelog.</p>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage title="Terms of use">
      <p>
        These terms cover q2cue.app, run by Brendan O'Leary. By creating an account you agree to them. Questions: <Mail />.
      </p>

      <h2>The service</h2>
      <p>
        q2cue.app lets you mark cues on script PDFs and make prompt books and cue sheets from them, alone or with people you invite. It's free to use right now. It's a small project run by one person: I'll do my best to keep it running and your data safe, but I can't promise it will always be available or free of bugs.
      </p>
      <p>
        Keep your own copies of anything you can't afford to lose. Download a show's bundle from Menu → Show settings, and export your prompt books before a performance.
      </p>

      <h2>Your account</h2>
      <ul>
        <li>Your account is tied to the email address and the sign-in method you first use. There's no way to change the email address or recover an account through the app; email <Mail /> if you're locked out.</li>
        <li>You're responsible for what happens under your account, so keep access to your email (or Google account) secure.</li>
        <li>You must be at least 13 years old to create an account.</li>
      </ul>

      <h2>Your content</h2>
      <ul>
        <li>You keep all rights to the scripts you upload and the work you create. q2cue only stores, displays and processes them to provide the service to you and the people you share a show with.</li>
        <li>Only upload scripts you have the right to use. Most scripts are copyrighted; using one in your own production under its licence is your and your production's responsibility.</li>
        <li>The owner of a show decides who can see and edit it. Everyone you add can see the whole show and download it.</li>
      </ul>

      <h2>Acceptable use</h2>
      <p>Don't use q2cue to:</p>
      <ul>
        <li>upload anything illegal, or anything you don't have the right to share with the people you invite;</li>
        <li>send invitations to people who haven't asked for them, or use it to send spam;</li>
        <li>try to get into accounts or shows you weren't given access to, or interfere with the service or other people's use of it;</li>
        <li>get around the usage limits (file size, number of shows, and how often you can send emails or exports).</li>
      </ul>
      <p>
        I may suspend or remove accounts that break these rules. Where I can, I'll tell you first and give you a chance to download your shows.
      </p>

      <h2>Ending</h2>
      <p>
        You can delete your account at any time from the shows page. If I ever shut q2cue.app down, I'll give at least 30 days' notice by email so you can download your shows. Because q2cue is open source, you can also run your own copy.
      </p>

      <h2>No warranty</h2>
      <p>
        q2cue.app is provided "as is", without warranties of any kind. To the extent the law allows, I'm not liable for lost data, missed cues, or other damages from using or being unable to use the service. Nothing in these terms limits rights you have by law that can't be limited.
      </p>

      <h2>The software</h2>
      <p>
        q2cue's code is open source under the MIT licence, and the q2cue data format is published under CC BY 4.0. These terms cover the hosted service at q2cue.app, not your use of the code.
      </p>

      <h2>Changes and law</h2>
      <p>
        If these terms change in a way that matters, I'll update the date at the top and email account holders before the change takes effect. These terms are governed by the laws of the State of Maryland, USA.
      </p>
    </LegalPage>
  );
}
